/**
 * Bundle Version Page
 *
 * Displays version and build metadata for bundles and pages
 * Helps diagnose cache issues and verify deployments
 */

import { useEffect, useState } from 'react';
import { useAppHeader } from '@room/shared-ui';

interface BundleInfo {
  appName: string;
  bundleUrl: string;
  loaded: boolean;
  loadedAt?: string;
}

interface VersionInfo {
  version?: string;
  buildTime?: string;
  commitHash?: string;
  [key: string]: any;
}

export default function BundleVersionPage() {
  const { setHeaderConfig } = useAppHeader();
  const [bundles, setBundles] = useState<BundleInfo[]>([]);
  const [pageVersion, setPageVersion] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setHeaderConfig({
      title: 'Bundle Versions',
      subtitle: 'Version and build metadata for bundles and pages',
    });
  }, [setHeaderConfig]);

  useEffect(() => {
    loadVersionInfo();
  }, []);

  const loadVersionInfo = async () => {
    setLoading(true);

    try {
      // 1. Load page version.json
      const timestamp = Date.now();
      const versionRes = await fetch(`/version.json?t=${timestamp}`);
      if (versionRes.ok) {
        const versionData = await versionRes.json();
        setPageVersion(versionData);
      }

      // 2. Check loaded bundles (from window global)
      const loadedBundles: BundleInfo[] = [];
      const apps = ['admin', 'chat', 'store', 'developer', 'plugins', 'home'];

      for (const app of apps) {
        const bundleUrl = `/api/bundles/${app}/app.js?v=${timestamp}`;
        const bundleName = `Room${app.charAt(0).toUpperCase() + app.slice(1)}Bundle`;
        const isLoaded = !!(window as any)[bundleName] || !!(window as any).RoomAppBundle;

        loadedBundles.push({
          appName: app,
          bundleUrl,
          loaded: isLoaded,
          loadedAt: isLoaded ? new Date().toISOString() : undefined,
        });
      }

      setBundles(loadedBundles);
    } catch (error) {
      console.error('Error loading version info:', error);
    } finally {
      setLoading(false);
    }
  };

  const forceReload = () => {
    window.location.reload();
  };

  const clearCacheAndReload = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Reload page
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <p>Loading version information...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      {/* Page Version */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
          Page Version (Admin App)
        </h2>
        {pageVersion ? (
          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            fontFamily: 'monospace',
            fontSize: '14px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(pageVersion, null, 2)}
            </pre>
          </div>
        ) : (
          <p style={{ color: '#6b7280' }}>No version.json found</p>
        )}
      </section>

      {/* Bundle Info */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
          Bundle Status
        </h2>
        <div style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                  App
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                  Bundle URL
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {bundles.map((bundle) => (
                <tr key={bundle.appName} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500' }}>
                    {bundle.appName}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', fontFamily: 'monospace', color: '#6b7280' }}>
                    {bundle.bundleUrl}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {bundle.loaded ? (
                      <span style={{
                        padding: '4px 8px',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        ✓ Loaded
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        background: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        Not Loaded
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Actions */}
      <section>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
          Actions
        </h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={forceReload}
            style={{
              padding: '10px 16px',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            🔄 Force Reload Page
          </button>
          <button
            onClick={clearCacheAndReload}
            style={{
              padding: '10px 16px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            🗑️ Clear All Caches & Reload
          </button>
          <button
            onClick={loadVersionInfo}
            style={{
              padding: '10px 16px',
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            ♻️ Refresh Version Info
          </button>
        </div>
      </section>

      {/* Debug Info */}
      <section style={{ marginTop: '32px', padding: '16px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>
          💡 Debug Tips
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e', fontSize: '14px', lineHeight: '1.6' }}>
          <li>Version.json shows current deployed version of this page</li>
          <li>Bundle URLs include cache buster timestamp (v=...)</li>
          <li>If bundles don't update, clear all caches and reload</li>
          <li>Check Network tab in DevTools to see actual requests</li>
          <li>Bundles are cached for 1 hour by router worker</li>
        </ul>
      </section>

      {/* Current Timestamp */}
      <section style={{ marginTop: '16px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
          Current Timestamp: {Date.now()} ({new Date().toISOString()})
        </p>
      </section>
    </div>
  );
}
